import { useState, useEffect } from 'react';

interface ViewportDimensions {
  height: number;
  isKeyboardVisible: boolean;
}

export const useKeyboardViewport = (): ViewportDimensions => {
  const [viewport, setViewport] = useState<ViewportDimensions>({
    height: window.innerHeight,
    isKeyboardVisible: false,
  });

  useEffect(() => {
    const initialHeight = window.innerHeight;
    let timeoutId: NodeJS.Timeout;

    const handleResize = () => {
      // Clear any existing timeout
      clearTimeout(timeoutId);
      
      // Debounce the resize events to avoid excessive updates
      timeoutId = setTimeout(() => {
        const currentHeight = window.innerHeight;
        const heightDifference = initialHeight - currentHeight;
        
        // Consider keyboard visible if height decreased by more than 150px
        // This threshold helps distinguish keyboard from browser UI changes
        const isKeyboardVisible = heightDifference > 150;
        
        setViewport({
          height: currentHeight,
          isKeyboardVisible,
        });
      }, 100);
    };

    // Use Visual Viewport API if available (modern browsers)
    if (window.visualViewport) {
      const handleVisualViewportChange = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          const currentHeight = window.visualViewport!.height;
          const heightDifference = initialHeight - currentHeight;
          const isKeyboardVisible = heightDifference > 150;
          
          setViewport({
            height: currentHeight,
            isKeyboardVisible,
          });
        }, 100);
      };

      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
      
      return () => {
        clearTimeout(timeoutId);
        window.visualViewport?.removeEventListener('resize', handleVisualViewportChange);
      };
    } else {
      // Fallback to window resize for older browsers
      window.addEventListener('resize', handleResize);
      
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  return viewport;
};
